import React from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft, Sparkles, Globe2, Star, Coins, Zap, Trophy, Gift, Dog,
  Gamepad2, Heart, Shield, Mail, ChevronRight, Lock, Flag,
} from 'lucide-react';

/**
 * Help Center — content sourced from the customer-supplied WorldVibe Help Center doc.
 * Single-source-of-truth: edit the SECTIONS array to update copy.
 * Each section becomes a styled card; FAQ-style sub-questions render as nested blocks.
 */

const RANK_LIST = [
  'Explorer', 'Traveler', 'Voyager', 'Astronaut', 'Cosmonaut', 'Star Commander',
  'Galactic Guardian', 'Nebula Master', 'Celestial Titan', 'Cosmic Sovereign',
  'Universal Architect', 'Supreme Being',
];

const SECTIONS = [
  {
    id: 'getting-started',
    icon: Sparkles, color: '#A78BFA',
    title: 'Getting Started',
    intro: 'WorldVibe is a real-time social discovery platform designed to connect people globally through chat, communities, games, gifts, and shared experiences.',
    blocks: [
      {
        q: 'What is WorldVibe?',
        bullets: [
          'Meet people worldwide',
          'Join Planets',
          'Make friends',
          'Send gifts',
          'Play mini-games',
          'Earn XP',
          'Unlock Cosmic Ranks',
          'Customize your profile and pets',
        ],
      },
      {
        q: 'What are Planets?',
        body: 'Planets are communities within WorldVibe. Each Planet is built around a topic, interest, region, or community.',
        bullets: ['🌍 Global Chat', '🎮 Gaming', '🎵 Music', '💕 Dating', '✈️ Travel', '🏏 Sports'],
        footer: 'Users can join Planets to chat and interact with others who share similar interests.',
      },
    ],
  },
  {
    id: 'economy',
    icon: Star, color: '#FBBF24',
    title: 'WorldVibe Economy',
    intro: 'WorldVibe uses three progression systems.',
    blocks: [
      {
        q: '⭐ Stars',
        body: 'Stars are the premium currency. Use them for:',
        bullets: ['Sending gifts', 'Buying cosmetics', 'Upgrading pets', 'Joining premium events', 'Special dating interactions'],
        footer: 'Stars may be earned through activities or purchased through supported payment methods.',
      },
      {
        q: '🌐 Credits',
        body: 'Credits are reward tokens earned through platform participation. Use them for:',
        bullets: ['Daily spin wheels', 'Mystery boxes', 'Event rewards', 'Basic cosmetics'],
        footer: 'Credits cannot be purchased directly.',
      },
      {
        q: '⚡ XP',
        body: 'XP (Experience Points) measure your activity within WorldVibe. Earn XP by:',
        bullets: ['Chatting', 'Joining Planets', 'Playing games', 'Completing daily missions', 'Participating in events'],
        footer: 'XP cannot be purchased.',
      },
    ],
  },
  {
    id: 'cosmic-rank',
    icon: Trophy, color: '#F472B6',
    title: 'Cosmic Rank System',
    intro: 'Cosmic Rank reflects your activity and contribution within WorldVibe. Unlike Stars, ranks cannot be purchased — you progress by earning XP.',
    blocks: [
      {
        q: 'The 12 Cosmic Ranks',
        custom: 'ranks',
      },
      {
        q: 'Why should I rank up?',
        body: 'Higher ranks unlock:',
        bullets: ['Profile prestige', 'Special badges', 'Exclusive profile frames', 'Increased Planet limits', 'Better daily rewards', 'Additional pet slots', 'Cosmetic upgrades'],
        footer: 'Ranking up is a sign of dedication and participation within the community.',
      },
    ],
  },
  {
    id: 'gifts',
    icon: Gift, color: '#FB7185',
    title: 'Gifts',
    blocks: [
      {
        q: 'What are Gifts?',
        body: 'Gifts are virtual items that can be sent to other users.',
        bullets: ['🌹 Rose', '🚀 Rocket', '👑 Crown', '🌌 Galaxy', '🪐 Planet', '💥 Big Bang'],
        footer: 'Gifts help users express appreciation and support.',
      },
      {
        q: 'What happens when I receive Gifts?',
        body: 'Receiving gifts may contribute toward:',
        bullets: ['XP progression', 'Profile prestige', 'Event rankings', 'Community recognition'],
      },
    ],
  },
  {
    id: 'pets',
    icon: Dog, color: '#34D399',
    title: 'Pets',
    blocks: [
      {
        q: 'What are Pets?',
        body: 'Pets are collectible companions that travel with you throughout the WorldVibe Universe. Higher Cosmic Ranks may unlock additional pet slots and customization options.',
      },
    ],
  },
  {
    id: 'games',
    icon: Gamepad2, color: '#22D3EE',
    title: 'Games & Cosmic Arcade',
    blocks: [
      {
        q: 'What games are available?',
        body: 'WorldVibe currently includes:',
        bullets: ['Quiz', 'Tic Tac Toe', 'Battleship'],
        footer: 'Additional games may be introduced in future updates.',
      },
      {
        q: 'Can I earn rewards from games?',
        body: 'Yes. Games may reward:',
        bullets: ['XP', 'Credits', 'Stars', 'Badges', 'Seasonal rewards'],
      },
    ],
  },
  {
    id: 'dating',
    icon: Heart, color: '#F472B6',
    title: 'Dating & Relationships',
    blocks: [
      {
        q: 'How does dating work?',
        body: 'Users may discover and connect with others through the Dating section. Relationship statuses may include:',
        bullets: ['Single', 'Dating', 'Engaged', 'Married'],
      },
      {
        q: 'Can I propose to another user?',
        body: 'Yes. Special gifts and interactions may be used to celebrate milestones within the platform.',
      },
    ],
  },
  {
    id: 'safety',
    icon: Shield, color: '#FB923C',
    title: 'Safety & Community Guidelines',
    intro: 'WorldVibe is built around respectful interaction.',
    blocks: [
      {
        q: 'What is NOT allowed',
        body: 'Users must not:',
        bullets: ['Harass others', 'Share illegal content', 'Impersonate individuals', 'Engage in hate speech', 'Scam or defraud users', 'Spam communities'],
        footer: 'Violations may result in warnings, suspensions, or permanent bans.',
      },
      {
        q: 'How do I report someone?',
        steps: [
          'Open the user\u2019s profile.',
          'Tap Report.',
          'Select a reason.',
          'Submit the report.',
        ],
        footer: 'Our moderation team reviews all reports.',
      },
      {
        q: 'Account Security',
        body: 'To keep your account secure:',
        bullets: [
          'Never share your password.',
          'Never share OTP codes.',
          'Use a strong password.',
          'Report suspicious activity immediately.',
        ],
        footer: 'WorldVibe staff will never ask for your password.',
      },
    ],
  },
];

const CONTACTS = [
  { icon: Mail, label: 'General support', email: 'support@worldvibe.online', color: '#A78BFA' },
  { icon: Lock, label: 'Security issues', email: 'security@worldvibe.online', color: '#F87171' },
  { icon: Flag, label: 'Business inquiries', email: 'hello@worldvibe.online', color: '#22D3EE' },
];

function Block({ block }) {
  return (
    <div className="rounded-2xl p-5 bg-white/3 border border-white/8" data-testid={`help-block-${block.q.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
      <div className="font-display font-semibold text-white text-base mb-2">{block.q}</div>
      {block.body && <p className="text-sm text-white/70 leading-relaxed">{block.body}</p>}
      {block.bullets && (
        <ul className="mt-2 grid sm:grid-cols-2 gap-y-1 gap-x-4">
          {block.bullets.map((b, i) => (
            <li key={i} className="text-sm text-white/80 flex items-start gap-2">
              <ChevronRight size={12} className="text-fuchsia-300 mt-1.5 shrink-0" />
              <span>{b}</span>
            </li>
          ))}
        </ul>
      )}
      {block.steps && (
        <ol className="mt-2 space-y-1.5">
          {block.steps.map((s, i) => (
            <li key={i} className="text-sm text-white/80 flex items-start gap-2.5">
              <span className="shrink-0 text-[11px] font-bold w-5 h-5 rounded-full bg-fuchsia-500/20 text-fuchsia-300 flex items-center justify-center">{i + 1}</span>
              <span className="pt-0.5">{s}</span>
            </li>
          ))}
        </ol>
      )}
      {block.custom === 'ranks' && (
        <div className="mt-2 flex flex-wrap gap-1.5" data-testid="help-rank-list">
          {RANK_LIST.map((r, i) => (
            <span
              key={r}
              className="text-[11px] font-semibold px-2.5 py-1 rounded-full"
              style={{
                color: `hsl(${(i * 30) % 360}, 70%, 78%)`,
                background: `hsla(${(i * 30) % 360}, 70%, 60%, 0.12)`,
                border: `1px solid hsla(${(i * 30) % 360}, 70%, 60%, 0.3)`,
              }}
            >
              {i + 1}. {r}
            </span>
          ))}
        </div>
      )}
      {block.footer && <p className="mt-3 text-xs text-white/50 italic">{block.footer}</p>}
    </div>
  );
}

export default function Help() {
  return (
    <div className="max-w-4xl mx-auto pb-12" data-testid="help-page">
      {/* Header */}
      <div className="flex items-center gap-3 mb-2">
        <Link
          to="/app/menu"
          className="w-9 h-9 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 flex items-center justify-center"
          data-testid="help-back"
          aria-label="Back to menu"
        >
          <ArrowLeft size={16} />
        </Link>
        <div>
          <h1 className="font-display font-bold text-3xl md:text-4xl">WorldVibe Help Center</h1>
          <p className="text-white/50 text-sm mt-0.5">Meet the World in Real Time — your guide to the universe ✨</p>
        </div>
      </div>

      {/* Hero blurb */}
      <div className="mt-5 rounded-3xl p-6 bg-gradient-to-br from-fuchsia-500/10 via-violet-500/8 to-cyan-500/10 border border-white/10">
        <div className="flex items-start gap-3">
          <Globe2 size={22} className="text-cyan-300 shrink-0 mt-0.5" />
          <p className="text-white/80 leading-relaxed text-sm">
            WorldVibe is a social platform where you can meet people from around the world, join communities called <strong className="text-white">Planets</strong>, play games, earn rewards, and rise through the <strong className="text-white">Cosmic Rank System</strong>. Whether you&apos;re a new <em>Explorer</em> or aiming to become a <em>Supreme Being</em>, this guide will help you navigate the universe.
          </p>
        </div>
      </div>

      {/* Quick links */}
      <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-2">
        {SECTIONS.map((s) => {
          const Icon = s.icon;
          return (
            <a
              key={s.id}
              href={`#${s.id}`}
              className="rounded-xl p-3 bg-white/4 border border-white/8 hover:bg-white/8 transition flex items-center gap-2"
              data-testid={`help-jump-${s.id}`}
            >
              <Icon size={14} style={{ color: s.color }} />
              <span className="text-xs font-semibold text-white/85 truncate">{s.title}</span>
            </a>
          );
        })}
      </div>

      {/* Sections */}
      <div className="mt-6 space-y-8">
        {SECTIONS.map((s) => {
          const Icon = s.icon;
          return (
            <section key={s.id} id={s.id} className="scroll-mt-20" data-testid={`help-section-${s.id}`}>
              <div className="flex items-center gap-2.5 mb-2">
                <div
                  className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0"
                  style={{ background: `${s.color}1F`, border: `1px solid ${s.color}40` }}
                >
                  <Icon size={18} style={{ color: s.color }} />
                </div>
                <h2 className="font-display font-bold text-xl md:text-2xl" style={{ color: s.color }}>{s.title}</h2>
              </div>
              {s.intro && <p className="text-sm text-white/65 mb-3 leading-relaxed">{s.intro}</p>}
              <div className="space-y-3">
                {s.blocks.map((b, i) => <Block key={i} block={b} />)}
              </div>
            </section>
          );
        })}
      </div>

      {/* Contact */}
      <section id="contact" className="mt-10" data-testid="help-section-contact">
        <div className="flex items-center gap-2.5 mb-3">
          <div className="w-9 h-9 rounded-xl bg-emerald-400/15 border border-emerald-400/40 flex items-center justify-center">
            <Mail size={18} className="text-emerald-300" />
          </div>
          <h2 className="font-display font-bold text-xl md:text-2xl text-emerald-300">Contact Support</h2>
        </div>
        <p className="text-sm text-white/65 mb-3">Need help? Reach out — we usually respond within a day.</p>
        <div className="grid sm:grid-cols-3 gap-3">
          {CONTACTS.map((c) => {
            const Icon = c.icon;
            return (
              <a
                key={c.email}
                href={`mailto:${c.email}`}
                className="rounded-2xl p-4 bg-white/3 border border-white/8 hover:bg-white/6 transition group"
                data-testid={`help-contact-${c.email.split('@')[0]}`}
              >
                <Icon size={16} style={{ color: c.color }} />
                <div className="mt-2 text-[11px] uppercase tracking-wider text-white/45 font-semibold">{c.label}</div>
                <div className="text-sm text-white/90 group-hover:text-white break-all">{c.email}</div>
              </a>
            );
          })}
        </div>
      </section>

      {/* Legal Documents */}
      <section id="legal" className="mt-10" data-testid="help-section-legal">
        <div className="flex items-center gap-2.5 mb-3">
          <div className="w-9 h-9 rounded-xl bg-fuchsia-400/15 border border-fuchsia-400/40 flex items-center justify-center">
            <Shield size={18} className="text-fuchsia-300" />
          </div>
          <h2 className="font-display font-bold text-xl md:text-2xl text-fuchsia-300">Legal & Policies</h2>
        </div>
        <p className="text-sm text-white/65 mb-3">The rules of the universe — read these to know how WorldVibe operates.</p>
        <div className="grid sm:grid-cols-3 gap-3">
          <Link to="/privacy" className="rounded-2xl p-4 bg-white/3 border border-white/8 hover:bg-white/6 transition" data-testid="help-link-privacy">
            <Lock size={16} className="text-cyan-300" />
            <div className="mt-2 text-[11px] uppercase tracking-wider text-white/45 font-semibold">Privacy</div>
            <div className="text-sm text-white/90">Privacy Policy</div>
          </Link>
          <Link to="/terms" className="rounded-2xl p-4 bg-white/3 border border-white/8 hover:bg-white/6 transition" data-testid="help-link-terms">
            <Shield size={16} className="text-pink-300" />
            <div className="mt-2 text-[11px] uppercase tracking-wider text-white/45 font-semibold">Legal</div>
            <div className="text-sm text-white/90">Terms of Service</div>
          </Link>
          <Link to="/guidelines" className="rounded-2xl p-4 bg-white/3 border border-white/8 hover:bg-white/6 transition" data-testid="help-link-guidelines">
            <Flag size={16} className="text-emerald-300" />
            <div className="mt-2 text-[11px] uppercase tracking-wider text-white/45 font-semibold">Community</div>
            <div className="text-sm text-white/90">Community Guidelines</div>
          </Link>
        </div>
      </section>

      <div className="mt-10 text-center text-xs text-white/30">
        WorldVibe Motto · <span className="text-white/50 italic">Meet the World in Real Time</span>
      </div>
    </div>
  );
}
